import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Mail, User, Eye, EyeOff, Shield, Copy, CheckCircle, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../App";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Seed phrase backup flow
  const [seedPhrase, setSeedPhrase] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [seedCopied, setSeedCopied] = useState(false);
  const [seedConfirmed, setSeedConfirmed] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      localStorage.setItem('auth_token', response.data.token);
      
      // Show seed phrase backup screen before navigating
      if (response.data.master_seed_phrase) {
        setSeedPhrase(response.data.master_seed_phrase);
        setRegistrationData(response.data);
      } else {
        // Fallback: no seed phrase returned (shouldn't happen)
        const walletCount = response.data.wallets_created || 0;
        toast.success(`Account created! ${walletCount} chain wallets ready`);
        navigate('/dashboard', { state: { user: response.data } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCopySeed = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(seedPhrase);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = seedPhrase;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setSeedCopied(true);
      toast.success("Seed phrase copied to clipboard");
    } catch {
      toast.error("Failed to copy — please write it down manually");
    }
  };

  const handleConfirmSeed = () => {
    const words = seedPhrase.split(" ");
    // Ask them to type word #3 and word #9 (human-indexed)
    const expected = `${words[2]} ${words[8]}`;
    if (confirmInput.trim().toLowerCase() === expected.toLowerCase()) {
      setSeedConfirmed(true);
    } else {
      toast.error(`Incorrect. Please enter word #3 and word #9 from your seed phrase, separated by a space.`);
    }
  };

  const handleProceedToDashboard = () => {
    const walletCount = registrationData?.wallets_created || 0;
    toast.success(`Account created! ${walletCount} chain wallets ready`, {
      description: "Your seed phrase is the ONLY way to recover your wallets"
    });
    navigate('/dashboard', { state: { user: registrationData } });
  };

  // Handle Google credential response
  const handleGoogleCredential = useCallback(async (response) => {
    setLoading(true);
    try {
      const result = await axios.post(`${API}/auth/google`, {
        id_token: response.credential
      });
      localStorage.setItem('auth_token', result.data.token);
      toast.success(`Welcome, ${result.data.name}!`);
      navigate('/dashboard', { state: { user: result.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Load Google Identity Services script
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true
      });
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [handleGoogleCredential]);

  const handleGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error("Google login not configured");
      return;
    }
    if (!window.google?.accounts?.id) {
      toast.error("Google Sign-In is loading, try again in a moment");
      return;
    }
    window.google.accounts.id.prompt();
  };

  // ==================== SEED PHRASE BACKUP SCREEN ====================
  if (seedPhrase) {
    const words = seedPhrase.split(" ");
    
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#020617]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-lg"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 mb-4">
              <Shield className="w-8 h-8 text-[#FFD700]" />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Syne'] mb-2">
              Back Up Your Seed Phrase
            </h1>
            <p className="text-white/50">
              This is the <span className="text-[#FFD700] font-semibold">only way</span> to recover your wallets. 
              Write it down and store it safely.
            </p>
          </div>

          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 mb-6">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-semibold text-sm">Do not share this with anyone</p>
              <p className="text-red-400/70 text-xs mt-1">
                Anyone with these 12 words has full access to all your wallets on all 12 chains. 
                Xavier does not store this — if you lose it, your funds are gone forever.
              </p>
            </div>
          </div>

          {/* Seed Phrase Grid */}
          <div className="p-6 rounded-2xl bg-white/5 border border-white/10 mb-4">
            <div className="grid grid-cols-3 gap-3">
              {words.map((word, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#002366]/50 border border-[#FFD700]/20"
                >
                  <span className="text-[#FFD700]/50 text-xs font-mono w-5 text-right">{i + 1}.</span>
                  <span className="text-white font-mono text-sm">{word}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Copy Button */}
          <Button
            onClick={handleCopySeed}
            variant="outline"
            className="w-full mb-6 border-white/20 text-white/70 hover:text-white hover:bg-white/5 h-11"
          >
            {seedCopied ? (
              <><CheckCircle className="w-4 h-4 mr-2 text-green-400" /> Copied to clipboard</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" /> Copy seed phrase</>
            )}
          </Button>

          {/* Confirmation Step */}
          {!seedConfirmed ? (
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
              <p className="text-white/70 text-sm mb-3">
                To confirm you've saved it, enter <span className="text-[#FFD700] font-semibold">word #3</span> and <span className="text-[#FFD700] font-semibold">word #9</span> separated by a space:
              </p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="word3 word9"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmSeed()}
                  className="input-quantum flex-1"
                  autoFocus
                />
                <Button
                  onClick={handleConfirmSeed}
                  className="bg-[#FFD700] text-[#002366] hover:bg-[#FDB931] px-6 font-semibold"
                >
                  Verify
                </Button>
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30 mb-6">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm font-medium">
                  Seed phrase verified! Your {registrationData?.wallets_created || 12} chain wallets are ready.
                </p>
              </div>
              
              <Button
                onClick={handleProceedToDashboard}
                className="w-full btn-gold rounded-lg h-12 text-lg font-semibold"
                data-testid="proceed-dashboard-btn"
              >
                Enter Dashboard
              </Button>
            </motion.div>
          )}

          {/* Wallet Summary */}
          {registrationData?.wallets && (
            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">Wallets Created</p>
              <div className="flex flex-wrap gap-2">
                {registrationData.wallets.map((w) => (
                  <span
                    key={w.chain}
                    className="px-2.5 py-1 rounded-md bg-[#FFD700]/10 border border-[#FFD700]/20 text-[#FFD700] text-xs font-mono"
                  >
                    {w.chain.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // ==================== REGISTRATION FORM ====================
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1718013102329-cc42f024bdf6?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#002366]/90 via-[#002366]/70 to-transparent" />
        <div className="relative z-10 flex flex-col items-center justify-center p-16 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="grid grid-cols-3 gap-3 mb-8">
              {['XRP', 'SOL', 'ETH', 'BTC', 'XLM', 'HBAR', 'AVAX', 'MATIC', 'ARB'].map((chain, i) => (
                <motion.div
                  key={chain}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.06 }}
                  className="px-4 py-3 rounded-xl border border-[#FFD700]/30 bg-[#FFD700]/10 backdrop-blur-sm text-center"
                >
                  <span className="text-[#FFD700] font-bold text-lg">{chain}</span>
                </motion.div>
              ))}
            </div>
            <h2 className="text-3xl font-bold text-white font-['Syne'] mb-4">
              12-Chain Support
            </h2>
            <p className="text-white/70 text-lg max-w-md">
              All wallets created instantly at registration. 
              One account, every chain, zero friction.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 mb-12 group">
            <Sparkles className="w-8 h-8 text-[#FFD700] group-hover:scale-110 transition-transform" />
            <span className="text-xl font-bold text-white font-['Syne']">
              <span className="text-[#FFD700]">X</span>avier
            </span>
          </Link>

          <h1 className="text-4xl font-bold text-white font-['Syne'] mb-2">
            Create Account
          </h1>
          <p className="text-white/50 mb-8">
            Start securing your crypto assets today
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-quantum pl-12"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-quantum pl-12"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-quantum pl-12 pr-12"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-white/30 text-xs">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full btn-gold rounded-lg h-12"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[#002366] border-t-transparent rounded-full animate-spin" />
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#020617] px-4 text-white/30 text-sm">OR</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full btn-outline-gold rounded-lg h-12 flex items-center justify-center gap-3"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-white/50">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FFD700] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
